<script lang="ts">
    import {
        VpCloseIcon,
        VpDeleteBinIcon,
    } from '@decathlon-experience/icons/svelte';
    import * as Sentry from '@sentry/browser';
    import { onMount } from 'svelte';
    import { writable } from 'svelte/store';

    import { trans } from '$assets/translator.js';
    import DeleteConfirmationModal from '$backoffice/components/DeleteConfirmationModal.svelte';
    import {
        imageOptimize,
        uploadImageToMedia,
    } from '$backoffice/views/MediaManager/utils';
    import cn from '$common/utils/cn';
    import Modal from '$saga/components/Modal.svelte';

    import FocalPointEditor from './FocalPointEditor.svelte';
    import ImageCropEditor from './ImageCropEditor.svelte';
    import MediaUpload from './MediaUpload.svelte';

    export let inputName: string;
    export let value: string = '';
    export let required: boolean = false;
    export let apiEndpoint: string;
    export let mediaBaseUrl;
    export let focalPointInputName: string = '';
    export let imageWidthInputName: string = '';
    export let imageHeightInputName: string = '';
    export let cropProxyUrl: string;

    let previewUrl: string | null = value ? `${value}` : null;
    let focalPoint: { x: number; y: number } = { x: 50, y: 50 };
    let imageWidth: number | null = null;
    let imageHeight: number | null = null;
    let uploadComponentError: string | null = null;
    const isModalOpen = writable(false);
    let parentForm: HTMLFormElement | null = null;
    let isUploadInProgress: boolean = false;
    let isFocalPointEditorOpen = false;

    let cropEditSrc: string | null = null;
    let cropEditCroppedFile: File | null = null;
    let cropEditCroppedBlobUrl: string = '';

    $: isUploadingPreview = previewUrl && previewUrl.startsWith('blob:');

    onMount(() => {
        let element = document.querySelector(
            `input[name="${inputName}"]`,
        )?.parentElement;
        while (element && element.tagName !== 'FORM') {
            element = element.parentElement;
        }
        parentForm = element as HTMLFormElement;
    });

    function disableFormSubmission(): void {
        if (parentForm) {
            const formId = parentForm.id;

            // Find submit buttons inside the form
            const submitButtonsInForm = parentForm.querySelectorAll(
                'button[type="submit"], input[type="submit"]',
            );

            // Find submit buttons outside the form that reference this form via form attribute
            const submitButtonsOutsideForm = formId
                ? document.querySelectorAll(
                      `button[type="submit"][form="${formId}"], input[type="submit"][form="${formId}"]`,
                  )
                : [];

            // Combine both sets of buttons
            const allSubmitButtons = [
                ...submitButtonsInForm,
                ...submitButtonsOutsideForm,
            ];

            allSubmitButtons.forEach((button) => {
                const btn = button as HTMLButtonElement | HTMLInputElement;
                // Only store original state if we haven't done so already
                if (
                    !Object.prototype.hasOwnProperty.call(
                        btn.dataset,
                        'originalDisabled',
                    )
                ) {
                    btn.dataset.originalDisabled = btn.disabled
                        ? 'true'
                        : 'false';
                }
                btn.disabled = true;
            });
        }
    }

    function enableFormSubmission(): void {
        if (parentForm) {
            const formId = parentForm.id;

            // Find submit buttons inside the form
            const submitButtonsInForm = parentForm.querySelectorAll(
                'button[type="submit"], input[type="submit"]',
            );

            // Find submit buttons outside the form that reference this form via form attribute
            const submitButtonsOutsideForm = formId
                ? document.querySelectorAll(
                      `button[type="submit"][form="${formId}"], input[type="submit"][form="${formId}"]`,
                  )
                : [];

            // Combine both sets of buttons
            const allSubmitButtons = [
                ...submitButtonsInForm,
                ...submitButtonsOutsideForm,
            ];

            allSubmitButtons.forEach((button) => {
                const btn = button as HTMLButtonElement | HTMLInputElement;
                // Restore the original disabled state (false means it was enabled)
                btn.disabled = btn.dataset.originalDisabled === 'true';
                // Clean up the dataset after restoring
                delete btn.dataset.originalDisabled;
            });
        }
    }

    const handleFileSelected = (
        event: CustomEvent<{
            url: string | null;
            error?: string | null;
            file?: File | null;
            focalPoint?: { x: number; y: number } | null;
            imageWidth?: number | null;
            imageHeight?: number | null;
        }>,
    ) => {
        const {
            url,
            error,
            focalPoint: fp,
            imageWidth: nextImageWidth,
            imageHeight: nextImageHeight,
        } = event.detail;

        if (fp) {
            focalPoint = fp;
        }

        if (nextImageWidth !== undefined) {
            imageWidth = nextImageWidth;
        }

        if (nextImageHeight !== undefined) {
            imageHeight = nextImageHeight;
        }

        if (url && url.startsWith('blob:') && !isUploadInProgress) {
            isUploadInProgress = true;
            disableFormSubmission();
        }

        if (
            isUploadInProgress &&
            ((url && !url.startsWith('blob:')) || error)
        ) {
            isUploadInProgress = false;
            enableFormSubmission();
        }

        if (
            previewUrl &&
            previewUrl.startsWith('blob:') &&
            previewUrl !== url
        ) {
            URL.revokeObjectURL(previewUrl);
        }
        previewUrl = url;
        uploadComponentError = error || null;
    };

    function handleRemoveImage(): void {
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        previewUrl = null;
        imageWidth = null;
        imageHeight = null;
        uploadComponentError = null;
    }

    function openModal(): void {
        if (!isUploadingPreview) {
            $isModalOpen = true;
        }
    }

    function closeModal(): void {
        $isModalOpen = false;
    }

    // Store the full URL in the hidden input when it's final (not a blob)
    $: hiddenInputValue =
        previewUrl && !previewUrl.startsWith('blob:') ? previewUrl : '';

    $: previewThumbnailUrl =
        previewUrl && !previewUrl.startsWith('blob:')
            ? imageOptimize(previewUrl, '400x400', 'avif')
            : previewUrl;

    $: previewModalUrl =
        previewUrl && !previewUrl.startsWith('blob:')
            ? imageOptimize(previewUrl, '1920x0', 'avif')
            : previewUrl;

    $: focalPointHiddenValue = `${focalPoint.x}% ${focalPoint.y}%`;
    $: imageWidthHiddenValue = imageWidth ?? '';
    $: imageHeightHiddenValue = imageHeight ?? '';

    // Modal state for delete confirmation
    let isDeleteModalOpen = writable(false);

    const openDeleteModal = () => {
        $isDeleteModalOpen = true;
    };

    const handleEditFocalPoint = () => {
        if (previewUrl && !previewUrl.startsWith('blob:')) {
            isFocalPointEditorOpen = true;
        }
    };

    const handleEditCrop = () => {
        if (previewUrl && !previewUrl.startsWith('blob:')) {
            cropEditSrc = previewUrl;
        }
    };

    const handleCropEditCropped = (event: CustomEvent<{ file: File }>) => {
        cropEditCroppedFile = event.detail.file;
        cropEditCroppedBlobUrl = URL.createObjectURL(cropEditCroppedFile);
    };

    const handleCropEditFocalPointConfirmed = async (
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) => {
        const newFocalPoint = event.detail.focalPoint;
        const file = cropEditCroppedFile!;

        URL.revokeObjectURL(cropEditCroppedBlobUrl);
        cropEditCroppedBlobUrl = '';
        cropEditCroppedFile = null;
        cropEditSrc = null;
        disableFormSubmission();

        try {
            const uploadedImage = await uploadImageToMedia(
                apiEndpoint,
                mediaBaseUrl,
                file,
            );
            previewUrl = uploadedImage.url;
            imageWidth = uploadedImage.width;
            imageHeight = uploadedImage.height;
            focalPoint = newFocalPoint;
        } catch (error) {
            console.error('Failed to re-upload cropped image:', error);
            Sentry.captureException(error);
        } finally {
            enableFormSubmission();
        }
    };

    const handleCropEditFocalPointCancelled = () => {
        URL.revokeObjectURL(cropEditCroppedBlobUrl);
        cropEditCroppedBlobUrl = '';
        cropEditCroppedFile = null;
        // cropEditFile still set → ImageCropper re-appears
    };

    const handleCropEditCancelled = () => {
        cropEditSrc = null;
    };

    const handleFocalPointConfirm = (
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) => {
        focalPoint = event.detail.focalPoint;
        isFocalPointEditorOpen = false;
    };
</script>

<div class="flex max-w-[320px] flex-col gap-m">
    <input type="hidden" name={inputName} value={hiddenInputValue} {required} />
    {#if focalPointInputName}
        <input
            type="hidden"
            name={focalPointInputName}
            value={focalPointHiddenValue}
        />
    {/if}
    {#if imageWidthInputName}
        <input
            type="hidden"
            name={imageWidthInputName}
            value={imageWidthHiddenValue}
        />
    {/if}
    {#if imageHeightInputName}
        <input
            type="hidden"
            name={imageHeightInputName}
            value={imageHeightHiddenValue}
        />
    {/if}

    {#if !previewUrl}
        <MediaUpload
            {apiEndpoint}
            on:fileSelected={handleFileSelected}
            {mediaBaseUrl}
            {cropProxyUrl}
        />
        {#if uploadComponentError}
            <p class="mt-xs text-sm text-status-negative">
                Erreur: {uploadComponentError}
            </p>
        {/if}
    {:else}
        <div class="group relative border border-border-quiet">
            <button
                type="button"
                on:click={openModal}
                class={cn(
                    'focus:ring-border-focus relative aspect-square w-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-offset-2',
                    isUploadingPreview ? 'cursor-default' : 'cursor-zoom-in',
                )}
                aria-label={isUploadingPreview
                    ? trans('form.multi_image_upload.image_uploading')
                    : `${trans('form.multi_image_upload.upload_preview')} - ${trans('form.multi_image_upload.enlarge')}`}
            >
                <img
                    src={previewThumbnailUrl}
                    alt={isUploadingPreview
                        ? trans('form.multi_image_upload.image_uploading')
                        : trans('form.multi_image_upload.upload_preview')}
                    class={cn('absolute inset-0 h-full w-full object-cover', {
                        'opacity-40': isUploadingPreview,
                    })}
                    style="object-position: {focalPoint.x}% {focalPoint.y}%;"
                    loading="lazy"
                    decoding="async"
                />
            </button>

            {#if isUploadingPreview}
                <div
                    class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-black bg-opacity-40"
                >
                    <svg
                        class="h-12 w-12 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                </div>
            {/if}

            {#if !isUploadingPreview}
                <div class="flex justify-center gap-xs py-xs">
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={handleEditFocalPoint}
                        title={trans(
                            'form.multi_image_upload.focal_point.title',
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="size-m"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <circle cx="12" cy="12" r="3" fill="currentColor" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={handleEditCrop}
                        title={trans('action.edit')}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="size-m"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                d="M6 2H2v4M18 2h4v4M6 22H2v-4M18 22h4v-4M9 2h6M9 22h6M2 9v6M22 9v6"
                            />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={openDeleteModal}
                    >
                        <VpDeleteBinIcon class="size-m text-status-negative" />
                        <span class="text-sm uppercase text-status-negative">
                            {trans('common.delete')}
                        </span>
                    </button>
                </div>
            {/if}
        </div>
    {/if}
</div>

<Modal isOpen={isModalOpen} onClose={closeModal}>
    <div slot="header" class="flex items-center justify-between">
        <h2 class="text-lg font-medium">
            {trans('form.multi_image_upload.image_preview')}
        </h2>
        <button on:click={closeModal}>
            <VpCloseIcon class="size-xl text-content-neutral" />
        </button>
    </div>
    {#if $isModalOpen && previewModalUrl && !isUploadingPreview}
        <div
            class="flex h-[calc(100vh-110px)] items-center justify-center p-xl"
        >
            <img
                src={previewModalUrl}
                alt="Full size preview"
                class="max-h-full max-w-full object-contain"
                decoding="async"
            />
        </div>
    {/if}
</Modal>

<DeleteConfirmationModal
    bind:isOpen={isDeleteModalOpen}
    on:confirm={() => handleRemoveImage()}
/>

<!-- FocalPoint Modal -->
{#if cropEditCroppedFile && cropEditCroppedBlobUrl}
    <FocalPointEditor
        src={cropEditCroppedBlobUrl}
        initialFocalPoint={{ x: 50, y: 50 }}
        on:confirm={handleCropEditFocalPointConfirmed}
        on:cancel={handleCropEditFocalPointCancelled}
    />
{:else if cropEditSrc}
    <!-- Crop Edit Modal -->
    {#key cropEditSrc}
        <ImageCropEditor
            src={cropEditSrc}
            {cropProxyUrl}
            on:cropped={handleCropEditCropped}
            on:cancel={handleCropEditCancelled}
        />
    {/key}
{:else if isFocalPointEditorOpen && previewUrl}
    <FocalPointEditor
        src={imageOptimize(previewUrl, '1920x0', 'avif')}
        initialFocalPoint={focalPoint}
        on:confirm={handleFocalPointConfirm}
        on:cancel={() => {
            isFocalPointEditorOpen = false;
        }}
    />
{/if}
